import { Button } from "@gigpro/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@gigpro/ui/DropdownMenu";
import { ChevronDown, Star } from "@gigpro/ui/Icons";
import { LinearProgress } from "@gigpro/ui/LinearProgress";
import { Skeleton } from "@gigpro/ui/Skeleton";
import React from "react";
import { times } from "remeda";
import type { SelectOption } from "types/utils";

export type PROfileReviewsProps = {
  positions: Array<SelectOption<number>>;
  selectedPosition: SelectOption<number> | undefined;
  setSelectedPosition: (value: SelectOption<number> | undefined) => void;
  data: {
    overall: number | null;
    count: number;
    byRating: Record<number, number>;
    byCategory: Record<string, number | null>;
  };
};

export const PROfileReviews: React.FC<PROfileReviewsProps> = ({
  positions,
  selectedPosition,
  setSelectedPosition,
  data: { overall, count, byRating, byCategory },
}) => {
  const hasReviews = count > 0;
  return (
    <>
      <div className="flex items-center">
        <h2 className="profile-title text-large font-semibold @sm/profile:text-extraLarge">Reviews</h2>
        {positions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="small"
                className="ml-0.5 gap-1 py-0.5 @sm/profile:pt-1 [&>svg]:data-[state=open]:rotate-180"
                data-testid="position_select"
              >
                {selectedPosition?.label ?? "All positions"}
                <ChevronDown className="size-3.5 transition-transform" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent className="w-32 p-1 font-regular text-black" side="bottom" align="start">
                <DropdownMenuItem onClick={() => setSelectedPosition(undefined)} data-testid="menuitem_position_all">
                  All positions
                </DropdownMenuItem>
                {positions.map(({ value, label }) => (
                  <DropdownMenuItem
                    key={value}
                    onClick={() => setSelectedPosition({ value, label })}
                    data-testid={`menuitem_position_${label}`}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        )}
        {hasReviews && overall != null && (
          <p className="ml-auto flex items-center gap-0.5 text-large font-semibold @sm/profile:text-extraLarge">
            <Star className="-mt-0.5 size-4 text-warning" />
            {overall.toFixed(1)}
          </p>
        )}
      </div>
      {hasReviews ? (
        <p className="mt-1 mb-4 text-gray @sm/profile:mb-6 @sm/profile:text-large">
          {count} people left reviews for this Pro
        </p>
      ) : (
        <p className="mt-1 text-medium-gray">No reviews given</p>
      )}

      {hasReviews && (
        <div
          className="flex flex-col-reverse justify-between gap-6 @3xl/profile:flex-row"
          data-testid={`ratings_${selectedPosition?.label ?? "all"}`}
        >
          <div className="grid flex-[1.5] grid-cols-[max-content_1fr_max-content] items-center gap-x-2 gap-y-3 @3xl/profile:max-w-96">
            {Object.entries(byRating)
              .sort((a, b) => Number(b[0]) - Number(a[0]))
              .map(([stars, percentage]) => (
                <React.Fragment key={stars}>
                  <p className="justify-self-end">{stars} Star</p>
                  <LinearProgress className="h-1.5 bg-light-gray" value={percentage * 100} />
                  <p className="font-semibold" data-testid={`ratingspercentage_${stars}stars`}>
                    {(percentage * 100).toFixed(0)}%
                  </p>
                </React.Fragment>
              ))}
          </div>
          <div className="grid flex-1 auto-rows-max grid-cols-[max-content_1fr_max-content] items-center gap-x-2 gap-y-3 @3xl/profile:max-w-80">
            {Object.entries(byCategory).map(([category, stars]) => {
              const filledStars = stars == null ? 0 : Math.round(stars);
              return (
                <React.Fragment key={category}>
                  <p>{category}</p>
                  <p className="justify-self-center font-semibold" data-testid={`rating_${category}`}>
                    {stars == null ? "—" : stars.toFixed(1)}
                  </p>
                  <div className="flex gap-0.5">
                    {times(5, (i) => (
                      <Star
                        key={i}
                        data-filled={i + 1 <= filledStars}
                        className="size-4 text-light-gray transition-colors duration-300 data-[filled=true]:text-warning @sm/profile:size-5"
                      />
                    ))}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export function PROfileReviewsSkeleton() {
  return (
    <>
      <div className="flex justify-between">
        <h2 className="text-large font-semibold @sm/profile:text-extraLarge">Reviews</h2>
        <Skeleton className="w-10 text-large @sm/profile:text-extraLarge" />
      </div>
      <Skeleton className="mt-1 mb-4 w-40 text-normal @sm/profile:mb-6 @sm/profile:text-large" />
      <div className="flex flex-col-reverse justify-between gap-6 @sm/profile:flex-row">
        <div className="grid flex-1 grid-cols-[max-content_1fr_max-content] items-center gap-x-2 gap-y-3 @sm/profile:max-w-96">
          {times(5, (i) => (
            <React.Fragment key={i}>
              <Skeleton className="w-8 text-normal" />
              <Skeleton variant="rounded" className="h-2 flex-1" />
              <Skeleton className="w-6 text-normal" />
            </React.Fragment>
          ))}
        </div>
        <div className="grid flex-1 grid-cols-[max-content_1fr_max-content] items-center gap-x-2 gap-y-3 @sm/profile:max-w-96">
          {times(3, (i) => (
            <React.Fragment key={i}>
              <Skeleton className="w-20 text-normal" />
              <Skeleton className="w-6 justify-self-center text-normal" />
              <Skeleton className="w-24 text-normal" />
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
}
